// =============================================================================
// PWA Banners — Offline, Install, Update prompts
// =============================================================================

import { useState } from "react";
import clsx from "clsx";

// ── Offline Banner ───────────────────────────────────────────────────────────

interface OfflineBannerProps {
  className?: string;
}

export function OfflineBanner({ className }: OfflineBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className={clsx(
        "fixed top-0 left-0 right-0 z-50 bg-[var(--warning)] text-white px-4 py-2 text-sm text-center font-medium",
        className
      )}
    >
      You are offline. Actions will be queued and sent when you reconnect.
      <button
        className="ml-3 underline font-semibold"
        onClick={() => setDismissed(true)}
      >
        Dismiss
      </button>
    </div>
  );
}

// ── Install Banner ───────────────────────────────────────────────────────────

interface InstallBannerProps {
  onInstall: () => void;
  showIOSInstructions: boolean;
  className?: string;
}

export function InstallBanner({
  onInstall,
  showIOSInstructions,
  className,
}: InstallBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className={clsx(
        "fixed bottom-4 left-4 right-4 z-50 card p-4 flex items-center gap-3 shadow-[var(--shadow-popover)] max-w-md mx-auto",
        className
      )}
    >
      <span className="text-2xl">📲</span>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">
          Install OELOX
        </p>
        <p className="text-xs text-[var(--text-secondary)]">
          {showIOSInstructions
            ? "Tap Share → Add to Home Screen"
            : "Add to your home screen for quick access"}
        </p>
      </div>
      {!showIOSInstructions && (
        <button className="btn-primary text-sm px-4 py-2" onClick={onInstall}>
          Install
        </button>
      )}
      <button
        className="btn-icon"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

// ── Update Prompt ────────────────────────────────────────────────────────────

interface UpdatePromptProps {
  onUpdate: () => void;
  className?: string;
}

export function UpdatePrompt({ onUpdate, className }: UpdatePromptProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className={clsx(
        "fixed bottom-4 left-4 right-4 z-50 card p-4 flex items-center gap-3 shadow-[var(--shadow-popover)] max-w-md mx-auto",
        className
      )}
    >
      <span className="text-2xl">🔄</span>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[var(--text-primary)]">
          Update available
        </p>
        <p className="text-xs text-[var(--text-secondary)]">
          A new version is ready. Update now?
        </p>
      </div>
      <button className="btn-primary text-sm px-4 py-2" onClick={onUpdate}>
        Update
      </button>
      <button
        className="btn-icon"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
